import type { SearchResponse } from '@algolia/client-search'
import type { AlgoliaEventItem } from '~/composables/types/algolia'

export const KITS_BROWSE_PER_PAGE = 20

/** When a parent (e.g. kits/[category]) provides browse state for schema + showcase */
export const KITS_BROWSE_PRODUCTS_KEY = Symbol('kitsBrowseProducts')

const priceAmountCents = (euros: number) => Math.round(euros * 100)

function buildPriceFilterString(
  priceMinEur: number | undefined,
  priceMaxEur: number | undefined
): string | undefined {
  const filterParts: string[] = []
  if (priceMinEur != null) {
    filterParts.push(
      `(default_price.amount >= ${priceAmountCents(priceMinEur)})`
    )
  }
  if (priceMaxEur != null) {
    filterParts.push(
      `(default_price.amount <= ${priceAmountCents(priceMaxEur)})`
    )
  }
  if (filterParts.length === 0) return undefined
  return filterParts.join(' AND ')
}

function parseKitsBrowsePageFromRoute(route: ReturnType<typeof useRoute>) {
  const raw = route.query.page
  const s = Array.isArray(raw) ? raw[0] : raw
  if (s == null || s === '') return 1
  const n = Number(s)
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : 1
}

export const useKitsBrowseProducts = () => {
  const searchClient = useAlgoliaRef()
  const { locale } = useI18n()
  const route = useRoute()

  const page = ref(parseKitsBrowsePageFromRoute(route))

  watch(
    () => route.query.page,
    () => {
      const next = parseKitsBrowsePageFromRoute(route)
      if (page.value !== next) {
        page.value = next
      }
    }
  )
  const categorySlug = ref<string | null>(null)
  const saleOnly = ref(false)
  const priceMinEur = ref<number | undefined>()
  const priceMaxEur = ref<number | undefined>()

  /** Per filter state — avoids sharing one payload across navigations with mismatched refs */
  const kitsBrowseAsyncKey = computed(
    () =>
      `kits-browse-algolia:${locale.value}:${categorySlug.value ?? 'all'}:p${page.value}:sale${saleOnly.value ? 1 : 0}:min${priceMinEur.value ?? 'x'}:max${priceMaxEur.value ?? 'x'}`
  )

  const { data, pending, error, refresh } = useAsyncData(
    kitsBrowseAsyncKey,
    async () => {
      const filterParts: string[] = []
      if (saleOnly.value) {
        filterParts.push('(has_promotions:true)')
      }
      const priceFilters = buildPriceFilterString(
        priceMinEur.value,
        priceMaxEur.value
      )
      if (priceFilters) {
        filterParts.push(priceFilters)
      }

      /** Narrow by Algolia category slug when user selects a Strapi chip */
      const categoryFacetFilters = categorySlug.value
        ? ([`categories.slug:${categorySlug.value}`] as string[])
        : undefined

      const mainQuery = {
        indexName: getBoxesIndexName(locale.value),
        hitsPerPage: KITS_BROWSE_PER_PAGE,
        page: page.value - 1,
        clickAnalytics: true,
        facets: ['has_promotions'],
        maxValuesPerFacet: 5,
        ...(categoryFacetFilters ? { facetFilters: categoryFacetFilters } : {}),
        ...(filterParts.length > 0
          ? { filters: filterParts.join(' AND ') }
          : {}),
      }

      return searchClient.search<AlgoliaEventItem>([mainQuery])
    },
    {
      watch: [page, categorySlug, saleOnly, priceMinEur, priceMaxEur, locale],
    }
  )

  watch(
    [categorySlug, saleOnly, priceMinEur, priceMaxEur, locale],
    () => {
      page.value = 1
    },
    { flush: 'sync' }
  )

  const firstResult = computed(
    () =>
      data.value?.results?.[0] as SearchResponse<AlgoliaEventItem> | undefined
  )

  const hasSaleItemsInScope = computed(() => {
    const res = firstResult.value
    if (!res) return true
    const fp = res.facets?.has_promotions as Record<string, number> | undefined
    if (fp != null) return (fp['true'] ?? 0) > 0
    return (res.nbHits ?? 0) === 0 ? false : true
  })

  watch(hasSaleItemsInScope, (has) => {
    if (!has && saleOnly.value) {
      saleOnly.value = false
    }
  })

  const products = computed(() => firstResult.value?.hits ?? [])

  const totalHits = computed(() => firstResult.value?.nbHits ?? 0)

  const totalPages = computed(() => {
    const nb = firstResult.value?.nbPages
    if (nb == null || nb < 1) return 1
    return nb
  })

  return {
    products,
    totalHits,
    totalPages,
    pending,
    error,
    refresh,
    page,
    perPage: KITS_BROWSE_PER_PAGE,
    categorySlug,
    saleOnly,
    priceMinEur,
    priceMaxEur,
    hasSaleItemsInScope,
  }
}

export type KitsBrowseProductsReturn = ReturnType<typeof useKitsBrowseProducts>
